import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  message,
  Modal,
} from 'antd';
import {
  UserOutlined,
  CarOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { Estimate, EstimateLineItem, Customer, Vehicle } from '../types';
import VehicleForm from './VehicleForm';
import api, { estimateApi, shopSettingsApi, customerApi, vehicleApi, laborRateApi } from '../services/api';
import { feesApi } from '../services/api/feesApi';
import dayjs from 'dayjs';
import { useDebounce } from '../hooks';
import { useIsMobile } from '../hooks/useIsMobile';
import PageRefreshWarning from './PageRefreshWarning';
import { EstimateFormProvider, useEstimateFormContext } from '../contexts/EstimateFormContext';
import { logEvent, newEventId } from '../services/eventLogger';
import { calculateEstimateTotals } from '../utils/estimateTotals';
import { EstimateHeader, EstimateFormFields, EstimateLineItems, EstimateNotesSection, useEstimateAI } from './Estimates';
import KnowledgeBaseSuggestions, { type KBSuggestion } from './Shared/KnowledgeBaseSuggestions';
import { buildLineItemFromSuggestion } from '../utils/suggestionToLineItem';
import EstimateInspectionSection from './DVI/EstimateInspectionSection';
import { QuickCatalogLinks } from './Shared/QuickCatalogLinks';
import RepairDataLookupButton from './Shared/RepairDataLookupButton';
import LookupQueuePanel from './Shared/LookupQueuePanel';
import { socketService } from '../services/socketService';
import { useBreakpointDown, BREAKPOINTS } from '../constants/breakpoints';
import { CustomerVehiclePanel } from './EstimateForm/CustomerVehiclePanel';
import { useGuardedEditor, writeStash, readStash } from '../contexts/SessionGuardContext';
import { rehydrateStashedDates } from '../session/stashDates';
import { reportSaveError } from '../utils/saveErrorNotice';
import { getErrorMessage } from '../utils/apiError';
import {
  confirmMileageDecrease,
  parseMileageDecreaseConflict,
  MileageConfirmation,
} from '../utils/mileageConfirm';

/** Estimate form fields bound to a DatePicker — see session/stashDates.ts. */
const ESTIMATE_STASH_DATE_FIELDS = ['valid_until'] as const;

const { Text } = Typography;

const EstimateForm: React.FC = () => (
  <EstimateFormProvider>
    <EstimateFormInner />
  </EstimateFormProvider>
);

const EstimateFormInner: React.FC = () => <DesktopEstimateForm />;

const DesktopEstimateForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  // Start collapsed on phones (< 640px), and on tablets (< 1024px) when editing an
  // existing estimate so the line items are visible without scrolling past the panel.
  // New estimates stay expanded so the customer can be picked first. Lazy init so it's
  // set once at mount, not reactively — crossing a breakpoint never auto-collapses typed data.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const w = window.innerWidth;
    const editing = window.location.pathname.includes('/edit');
    return w < BREAKPOINTS.sm || (w < BREAKPOINTS.lg && editing);
  });
  const isMobile = useIsMobile(768);
  const asTopCard = useBreakpointDown('lg');   // <1024 → top card
  const railNarrow = useBreakpointDown('xl');  // 1024–1280 → narrower rail
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [lineItems, setLineItems] = useState<EstimateLineItem[]>([]);
  const [taxRate, setTaxRate] = useState(7);
  const [taxRateInput, setTaxRateInput] = useState('7');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draftEstimateId, setDraftEstimateId] = useState<number | null>(null);
  // Bumped when a lookup is queued, so the queue panel remounts and shows the new
  // job immediately rather than on its next five-second poll.
  const [queueSeq, setQueueSeq] = useState(0);
  const [draftEstimateNumber, setDraftEstimateNumber] = useState<string | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
  const [editVehicleModalOpen, setEditVehicleModalOpen] = useState(false);
  const [editCustomerForm] = Form.useForm();
  const [defaultLaborRate, setDefaultLaborRate] = useState<number>(175);
  // Quote-request linkage: retained when this form is opened from the Quote
  // Requests page so the created estimate can be tied back to the request.
  const [quoteRequestId, setQuoteRequestId] = useState<number | null>(null);
  const prefillAppliedRef = useRef(false);

  useEffect(() => {
    // Default labor rate for new/AI line items = the shop's DEFAULT named rate
    // (the current source of truth). Fall back to the legacy shop_settings
    // labor_rate, then the hardcoded default, so a shop with no named rates
    // configured still gets a sensible value.
    laborRateApi.getAll()
      .then((res) => {
        const rates = res.data ?? [];
        const def = rates.find((r) => r.is_default) ?? rates[0];
        if (def?.hourly_rate) {
          setDefaultLaborRate(def.hourly_rate);
        } else {
          throw new Error('no named labor rate');
        }
      })
      .catch(() => {
        shopSettingsApi.get()
          .then((res) => { if (res.data?.labor_rate) setDefaultLaborRate(res.data.labor_rate); })
          .catch(() => {});
      });
  }, []);

  const formContext = useEstimateFormContext();
  const { aiAnalysis, intake_odometer, internal_notes, setInternalNotes } = formContext;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  // Role-prefix-aware navigation (C1-T4): the form lives at /owner/estimates/...,
  // /advisor/estimates/..., or the legacy /admin/estimates/... — redirect targets
  // must respect the current prefix instead of always going to /admin.
  const rolePrefix = location.pathname.startsWith('/owner/')
    ? '/owner'
    : location.pathname.startsWith('/advisor/')
    ? '/advisor'
    : '/admin';
  const debouncedTaxRate = useDebounce(taxRate, 300);

  // AI hook — all AI-related callbacks and state
  const ai = useEstimateAI({
    form, formContext, estimate, lineItems, setLineItems,
    selectedCustomer, setSelectedCustomer, selectedVehicle, setSelectedVehicle,
    setHasUnsavedChanges, defaultLaborRate,
  });

  // Derived estimate id — used by socket listener and Request Customer Info button
  const currentEstimateId: number | undefined = (isEditing ? Number(id) : draftEstimateId) || undefined;

  // Document-level fees for the current estimate, split by taxability, so the
  // footer Total matches the server-stored (fee-inclusive) total. FeesEditor shows
  // the fee row in the same footer; without this the Total silently excluded them.
  const [estimateFees, setEstimateFees] = useState<{ taxable: number; nonTaxable: number }>({ taxable: 0, nonTaxable: 0 });
  useEffect(() => {
    if (!currentEstimateId) { setEstimateFees({ taxable: 0, nonTaxable: 0 }); return; }
    let cancelled = false;
    feesApi.list('estimates', currentEstimateId)
      .then((rows) => {
        if (cancelled) return;
        const split = { taxable: 0, nonTaxable: 0 };
        for (const f of rows || []) {
          const amount = Number(f?.computed_amount ?? (f?.calculation_method === 'flat' ? f?.value : 0)) || 0;
          if (f?.is_taxable) split.taxable += amount;
          else split.nonTaxable += amount;
        }
        setEstimateFees(split);
      })
      .catch(() => { if (!cancelled) setEstimateFees({ taxable: 0, nonTaxable: 0 }); });
    return () => { cancelled = true; };
  }, [currentEstimateId]);

  useEffect(() => {
    if (isEditing && id) fetchEstimate(parseInt(id));
  }, [isEditing, id]);

  // Prefill from router state (e.g. the Quote Requests page navigates to
  // /estimates/new with { customerId, vehicleId, description, quoteRequestId }).
  // Only act on a NEW estimate, only when state fields are present, and only
  // once — a normal blank new-estimate load is unaffected.
  useEffect(() => {
    if (isEditing) return;
    if (prefillAppliedRef.current) return;
    const state = location.state as
      | { customerId?: number; vehicleId?: number; description?: string; quoteRequestId?: number }
      | null;
    if (!state || (!state.customerId && !state.vehicleId && !state.description && !state.quoteRequestId)) return;
    prefillAppliedRef.current = true;

    if (typeof state.quoteRequestId === 'number') setQuoteRequestId(state.quoteRequestId);

    if (state.description) {
      form.setFieldValue('customer_notes', state.description);
      setHasUnsavedChanges(true);
    }

    if (typeof state.customerId === 'number') {
      customerApi.getById(state.customerId)
        .then((res) => {
          const customer = res.data;
          setSelectedCustomer(customer);
          form.setFieldValue('customer_id', customer.id);
        })
        .catch(() => {/* customer no longer available — leave selection empty */});
    }

    if (typeof state.vehicleId === 'number') {
      vehicleApi.getById(state.vehicleId)
        .then((res) => {
          const vehicle = res.data;
          setSelectedVehicle(vehicle);
          form.setFieldValue('vehicle_id', vehicle.id);
        })
        .catch(() => {/* vehicle no longer available — leave selection empty */});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, location.state]);

  // Socket listener — re-fetch when the Chrome extension dispatches items (C3-T19)
  useEffect(() => {
    if (!currentEstimateId) return;
    socketService.joinEntityChannel('estimate', currentEstimateId);
    // Notify Chrome extension of current entity target
    window.postMessage(
      { type: 'cnf_target', entity: { type: 'estimate', id: currentEstimateId } },
      window.location.origin
    );
    const unsubscribe = socketService.onDispatchItemsAdded((evt) => {
      if (evt.entity_type === 'estimate' && evt.entity_id === currentEstimateId) {
        message.success(`Imported ${evt.count} item${evt.count === 1 ? '' : 's'} from ${evt.source}`);
        fetchEstimate(currentEstimateId);
      }
    });
    return () => { unsubscribe(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEstimateId]);

  // Sync internal_notes from context → form field so save payload picks it up
  useEffect(() => {
    form.setFieldValue('internal_notes', internal_notes);
  }, [internal_notes, form]);

  const fetchEstimate = async (estimateId: number) => {
    try {
      setLoading(true);
      const response = await estimateApi.getById(estimateId);
      const found = response.data;
      setEstimate(found);
      setSelectedCustomer(found.customer || null);
      setSelectedVehicle(found.vehicle || null);
      setLineItems(found.line_items || []);
      form.setFieldsValue({
        customer_notes: found.customer_notes, internal_notes: found.internal_notes,
        valid_until: found.valid_until ? dayjs(found.valid_until) : null,
        customer_id: found.customer_id, vehicle_id: found.vehicle_id,
      });
    } catch (error) {
      console.error('Error fetching estimate:', error);
      message.error('Failed to fetch estimate details');
      navigate(`${rolePrefix}/estimates`);
    } finally {
      setLoading(false);
    }
  };

  // ── Customer/Vehicle handlers ──
  const handleCustomerChange = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    form.setFieldValue('customer_id', customer.id);
    setHasUnsavedChanges(true);
    formContext.markFieldDirty('customer.first_name');
    formContext.markFieldDirty('customer.last_name');
    if (selectedVehicle && selectedVehicle.customer_id !== customer.id) {
      setSelectedVehicle(null);
      form.setFieldValue('vehicle_id', undefined);
    }
    formContext.onCustomerSaved(customer);
  }, [formContext, form, selectedVehicle]);

  const handleVehicleChange = useCallback((vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    form.setFieldValue('vehicle_id', vehicle.id);
    setHasUnsavedChanges(true);
    formContext.markFieldDirty('vehicle.year');
    formContext.markFieldDirty('vehicle.make');
    formContext.markFieldDirty('vehicle.model');
  }, [form, formContext]);

  const handleClearCustomer = useCallback(() => {
    setSelectedCustomer(null); form.setFieldValue('customer_id', undefined);
    setSelectedVehicle(null); form.setFieldValue('vehicle_id', undefined);
  }, [form]);

  const handleClearVehicle = useCallback(() => {
    setSelectedVehicle(null); form.setFieldValue('vehicle_id', undefined);
  }, [form]);

  const handleOpenEditCustomer = useCallback(() => {
    if (selectedCustomer) { editCustomerForm.setFieldsValue(selectedCustomer); setEditCustomerModalOpen(true); }
  }, [selectedCustomer, editCustomerForm]);

  const handleSaveEditCustomer = async () => {
    try {
      const values = await editCustomerForm.validateFields();
      await customerApi.update(selectedCustomer!.id!, values);
      const response = await customerApi.getById(selectedCustomer!.id!);
      setSelectedCustomer(response.data);
      setEditCustomerModalOpen(false);
      message.success('Customer updated');
    } catch { message.error('Failed to update customer'); }
  };

  const handleEditVehicleSuccess = async () => {
    if (selectedVehicle?.id) {
      const response = await vehicleApi.getById(selectedVehicle.id);
      setSelectedVehicle(response.data);
    }
    setEditVehicleModalOpen(false);
    message.success('Vehicle updated');
  };

  const handleLineItemsChange = useCallback((items: EstimateLineItem[]) => {
    setLineItems(items); setHasUnsavedChanges(true);
  }, []);

  const handleAddSuggestion = useCallback((suggestion: KBSuggestion) => {
    // One-tap repeat-match autofill: labor + rate + common parts + last note.
    setLineItems(prev => [
      ...prev,
      buildLineItemFromSuggestion(suggestion, defaultLaborRate),
    ]);
    setHasUnsavedChanges(true);
  }, [defaultLaborRate]);

  const handleTaxRateChange = (value: string) => {
    setTaxRateInput(value); setTaxRate(parseFloat(value) || 0); setHasUnsavedChanges(true);
  };

  // ── Save ──
  const generateEstimateNumber = () => `CNF-EST-${new Date().getFullYear().toString().slice(-2)}-${String(Date.now()).slice(-5)}`;
  const getTwoWeeksFromNow = () => new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  const calculateTotals = () => {
    // Pass the document fees so the footer Total is fee-inclusive, mirroring the
    // backend calculateDocumentTotal (taxable fees join the tax base + total).
    // The FeesEditor already renders the fee row between Subtotal and Tax, so
    // Subtotal + Fees + Tax now reconciles to this Total.
    const totals = calculateEstimateTotals(lineItems, debouncedTaxRate, undefined, undefined, estimateFees);
    return { subtotal: totals.discountedSubtotal, taxAmount: totals.taxAmount, total: totals.total };
  };
  const getDefaultDisclaimer = () => "ESTIMATE DISCLAIMER: This estimate is provided for informational purposes only and is subject to change without notice. Final pricing may vary due to unforeseen circumstances including but not limited to: fluctuations in parts pricing, discovery of additional repairs required, complications arising from previous improper repairs, modifications to the vehicle, rusted, broken, or seized components, and other conditions not apparent during initial inspection. Any work performed outside the scope or price range of this estimate will require customer approval before proceeding. In the event that increased labor costs are not approved, alternative options will be provided to restore the vehicle to its pre-service condition. By accepting this estimate, the customer acknowledges and agrees to these terms and conditions. This estimate is valid for the period specified and may be withdrawn or revised thereafter.";

  const handleSave = async (
    status: 'draft' | 'sent' = 'draft',
    opts?: { navigateAfter?: boolean },
    // WP-08: present only on the retry after the user confirms a backwards
    // odometer reading (see the 409 handling in the catch below).
    mileageConfirmation?: MileageConfirmation
  ): Promise<number | undefined> => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const { subtotal, taxAmount, total } = calculateTotals();
      const estimateData: any = {
        customer_id: selectedCustomer?.id || null, vehicle_id: selectedVehicle?.id || null,
        customer_notes: values.customer_notes, internal_notes: values.internal_notes,
        intake_odometer: intake_odometer,
        line_items: lineItems, parts: [],
        status: (!selectedCustomer || !selectedVehicle) ? 'draft' : status,
        subtotal, tax_rate: taxRate, tax_amount: taxAmount, total_amount: total,
        valid_until: values.valid_until?.toISOString() || getTwoWeeksFromNow(),
        disclaimer: getDefaultDisclaimer(),
        ...(mileageConfirmation ?? {}),
      };
      let savedEstimateId: number | undefined;
      if (isEditing) {
        estimateData.estimate_number = estimate?.estimate_number;
        await estimateApi.update(estimate?.id!, estimateData);
        savedEstimateId = estimate?.id;
        message.success(`Estimate ${status === 'draft' ? 'saved' : 'sent'} successfully`);
      } else if (draftEstimateId) {
        // Promoting an auto-saved draft: preserve its existing estimate_number
        // (tracked from the saveDraft response); only mint one if absent.
        estimateData.estimate_number = draftEstimateNumber || estimate?.estimate_number || generateEstimateNumber();
        await estimateApi.update(draftEstimateId, estimateData);
        savedEstimateId = draftEstimateId;
        message.success(`Estimate ${status === 'draft' ? 'saved' : 'sent'} successfully`);
      } else {
        estimateData.estimate_number = generateEstimateNumber();
        const created = await estimateApi.create(estimateData);
        savedEstimateId = created.data?.id;
        message.success(`Estimate ${status === 'draft' ? 'created' : 'created and sent'} successfully`);
      }
      logEvent({ event_id: newEventId(), event_type: 'estimate.saved', entity_type: 'estimate', entity_id: savedEstimateId, source: 'manual' });
      // If this estimate originated from a quote request, link the two so the
      // request shows as fulfilled. The link endpoint lives outside the shared
      // axios baseURL (/api/quote-requests, not /api/admin), so it is reached
      // with an absolute path. Best-effort: a failure must not block the save.
      if (quoteRequestId && savedEstimateId) {
        try {
          const root = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
          await api.patch(
            `${root}/quote-requests/admin/${quoteRequestId}/link-estimate`,
            { estimate_id: savedEstimateId }
          );
        } catch (linkErr) {
          console.error('Failed to link quote request to estimate:', linkErr);
        }
      }
      setHasUnsavedChanges(false);
      // Send actions save in place (navigateAfter: false) so the user stays on
      // the editor after firing a send; the Save button still returns to the list.
      if (opts?.navigateAfter !== false) {
        navigate(`${rolePrefix}/estimates`);
      }
      return savedEstimateId;
    } catch (error: any) {
      // WP-08: a 409 means the odometer went backwards. Rollbacks and cluster
      // swaps are real, so ask the user to confirm with a reason and retry —
      // rather than losing the whole save over it.
      const decrease = parseMileageDecreaseConflict(error);
      if (decrease && !mileageConfirmation) {
        setLoading(false);
        const confirmed = await confirmMileageDecrease(decrease);
        if (!confirmed) return undefined;
        return handleSave(status, opts, confirmed);
      }

      console.error('Error saving estimate:', error);
      // WP-03. Was `message.error(data?.error || 'Failed to save estimate')`,
      // which told the owner nothing when the real cause was a missing
      // customer field — and rendered `[object Object]` when the central
      // handler fired, because an object is truthy and the `||` never ran.
      reportSaveError(message, error, {
        fallback: 'Failed to save estimate',
        // Focus the first offending control so "Street address is required"
        // is followed by the cursor actually being in that box.
        focusField: (name) => form.scrollToField(name, { behavior: 'smooth', block: 'center' }),
      });
      return undefined;
    } finally { setLoading(false); }
  };

  // Persist current edits without leaving the editor, returning the saved id.
  // Used by the Send dropdown so one press saves-then-sends in place. Defined as
  // a plain function (NOT memoized) so it always calls the current handleSave —
  // a useCallback([]) here captured the first-render closure when `estimate` was
  // still null and PUT'd to /estimates/undefined.
  const handleSaveAndStay = () => handleSave('draft', { navigateAfter: false });

  // Tier B — register this editor with the session guard so graceful-exit can
  // attempt a validated server save before logout, with a local stash fallback.
  const sessionStashKey = isEditing ? `estimate-${estimate?.id}` : 'estimate-new';
  useGuardedEditor(sessionStashKey, {
    isDirty: () => hasUnsavedChanges,
    validateAndSave: async () => {
      try {
        await form.validateFields();
        const savedId = await handleSave('draft', { navigateAfter: false });
        if (savedId == null) return { saved: false };
        return { saved: true, recordId: savedId };
      } catch {
        return { saved: false };
      }
    },
    stash: () => writeStash(sessionStashKey, { values: form.getFieldsValue(true), route: window.location.pathname }),
    // `valid_until` is bound to a DatePicker (EstimateNotesSection) and the
    // stash is JSON, so it comes back as a string and crashes rc-picker unless
    // it is turned back into a dayjs. See session/stashDates.ts.
    restore: () => {
      const v = readStash(sessionStashKey);
      if (!v?.values) return false;
      form.setFieldsValue(rehydrateStashedDates(v.values, ESTIMATE_STASH_DATE_FIELDS));
      return true;
    },
  });

  const handleAISave = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const values = await form.validateFields().catch(() => ({}));
      const { subtotal, taxAmount, total } = calculateTotals();
      const estimateData: any = {
        customer_id: selectedCustomer?.id || null, vehicle_id: selectedVehicle?.id || null,
        customer_notes: (values as any).customer_notes, internal_notes: (values as any).internal_notes,
        line_items: lineItems, parts: [], status: 'draft',
        subtotal, tax_rate: taxRate, tax_amount: taxAmount, total_amount: total,
        valid_until: (values as any).valid_until?.toISOString() || getTwoWeeksFromNow(),
        disclaimer: getDefaultDisclaimer(),
      };
      if (isEditing) { estimateData.estimate_number = estimate?.estimate_number; await estimateApi.update(estimate?.id!, estimateData); }
      else { estimateData.estimate_number = generateEstimateNumber(); await estimateApi.create(estimateData); }
      logEvent({ event_id: newEventId(), event_type: 'estimate.saved', entity_type: 'estimate', entity_id: estimate?.id, source: 'ai_interactive' });
      setHasUnsavedChanges(false);
    } catch (error: any) {
      // Same classification, but this path re-throws for its caller to render.
      throw new Error(getErrorMessage(error, 'Failed to save estimate'));
    } finally { setLoading(false); }
  }, [selectedCustomer, selectedVehicle, lineItems, form, taxRate, isEditing, estimate]);

  // ── Auto-save draft ──
  useEffect(() => {
    if (lineItems.length === 0) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        // Only persist line items that are complete enough to validate — a
        // partially-typed item (blank description) would 400 the draft schema.
        // It auto-saves on the next tick once its description is filled.
        const saveableItems = lineItems.filter(
          (li) => (li.description ?? '').trim().length > 0
        );
        if (saveableItems.length === 0) { setAutoSaveStatus('idle'); return; }
        setAutoSaveStatus('saving');
        const totals = calculateEstimateTotals(saveableItems, taxRate);
        const res = await estimateApi.saveDraft({
          id: isEditing ? Number(id) : draftEstimateId ?? undefined,
          customer_id: selectedCustomer?.id ?? null, vehicle_id: selectedVehicle?.id ?? null,
          line_items: saveableItems, subtotal: totals.discountedSubtotal,
          tax_rate: taxRate, tax_amount: totals.taxAmount, total_amount: totals.total,
          status: 'draft', valid_until: getTwoWeeksFromNow(),
        });
        if (res.data?.id && !isEditing) {
          setDraftEstimateId(res.data.id);
          if (res.data.estimate_number) setDraftEstimateNumber(res.data.estimate_number);
        }
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      } catch { setAutoSaveStatus('idle'); }
    }, 2000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, selectedCustomer?.id, selectedVehicle?.id, isEditing, taxRate, draftEstimateId]);

  // Create a draft on demand so condition photos can be attached during creation
  // (photos need a persisted estimate id; autosave only fires once a line item
  // has a description). Reuses the same draft-save path.
  const handleStartDraftForPhotos = useCallback(async () => {
    try {
      setAutoSaveStatus('saving');
      const saveable = lineItems.filter((li) => (li.description ?? '').trim().length > 0);
      const totals = calculateEstimateTotals(saveable, taxRate);
      const res = await estimateApi.saveDraft({
        id: draftEstimateId ?? undefined,
        customer_id: selectedCustomer?.id ?? null,
        vehicle_id: selectedVehicle?.id ?? null,
        line_items: saveable,
        subtotal: totals.discountedSubtotal,
        tax_rate: taxRate,
        tax_amount: totals.taxAmount,
        total_amount: totals.total,
        status: 'draft',
        valid_until: getTwoWeeksFromNow(),
      });
      if (res.data?.id) {
        setDraftEstimateId(res.data.id);
        if (res.data.estimate_number) setDraftEstimateNumber(res.data.estimate_number);
      }
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    } catch {
      setAutoSaveStatus('idle');
      message.error('Could not start a draft to add photos. Try adding a service line first.');
    }
  }, [lineItems, taxRate, draftEstimateId, selectedCustomer?.id, selectedVehicle?.id, message]);

  const { subtotal, taxAmount, total } = calculateTotals();

  // ── AI Chat props (shared between mobile/desktop) ──
  const aiChatProps = {
    serviceItems: lineItems, onAnalyze: ai.handleAIExtraction,
    onCustomerAutoSelected: ai.handleCustomerAutoSelected, onCustomerCandidatesFound: ai.handleCustomerCandidatesFound,
    onVehicleAutoSelected: ai.handleVehicleAutoSelected, onVehicleCandidatesFound: ai.handleVehicleCandidatesFound,
    existingCustomerId: selectedCustomer?.id,
    onCustomerFieldChange: ai.handleAICustomerFieldChange, onVehicleFieldChange: ai.handleAIVehicleFieldChange,
    onServiceItemChange: ai.handleAIServiceItemChange, onAddServiceItem: ai.handleAIAddServiceItem,
    onRemoveServiceItem: ai.handleAIRemoveServiceItem, onSave: handleAISave,
    onCustomerSearch: ai.handleAICustomerSearch, onVehicleSearch: ai.handleAIVehicleSearch,
    hasCustomer: !!selectedCustomer, disabled: loading,
  };

  // ── C&V panel props — built once, consumed by both rail and top-card branches ──
  const cvProps = {
    selectedCustomer,
    selectedVehicle,
    onCustomerChange: handleCustomerChange,
    onClearCustomer: handleClearCustomer,
    onEditCustomer: handleOpenEditCustomer,
    onVehicleChange: handleVehicleChange,
    onClearVehicle: handleClearVehicle,
    onEditVehicle: () => setEditVehicleModalOpen(true),
    aiChatProps,
    customerCandidates: ai.customerCandidates,
    onAcceptCustomerCandidate: ai.handleAcceptCustomerCandidate,
    onDismissCustomerCandidates: ai.handleDismissCustomerCandidates,
    vehicleCandidates: ai.vehicleCandidates,
    onAcceptVehicleCandidate: ai.handleAcceptVehicleCandidate,
    onDismissVehicleCandidates: ai.handleDismissVehicleCandidates,
    isEditing,
    estimateId: id,
    draftEstimateId,
    autoSaveStatus,
    onStartDraftForPhotos: handleStartDraftForPhotos,
    currentEstimateId,
  };

  return (
    <>
      <PageRefreshWarning enabled={hasUnsavedChanges && !loading} />
      <AnimatePresence mode="wait">
        {aiAnalysis.phase === 'analyzing' && (
          <motion.div key="ai-analyzing-banner" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8, transition: { duration: 0.2, ease: 'easeOut' } }} transition={{ duration: 0.2 }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, backgroundColor: '#1677FF',
              color: 'white', padding: '8px 24px', textAlign: 'center', fontSize: '14px', fontWeight: 500,
              pointerEvents: 'none', animation: 'ai-banner-pulse 1.5s ease-in-out infinite' }}>
            AI analyzing notes...
          </motion.div>
        )}
      </AnimatePresence>
      <style>{`@keyframes ai-banner-pulse { 0%, 100% { opacity: 0.75; } 50% { opacity: 1; } }`}</style>

      <div style={{ display: 'flex', flexDirection: asTopCard ? 'column' : 'row', height: isMobile ? 'calc(100vh - 52px)' : '100vh', width: '100%', backgroundColor: '#ffffff' }}>
        {/* ── Customer & Vehicle wrapper — ALWAYS MOUNTED, always first flex child.
             Layout morphs via CSS (flexDirection row→column) so CustomerVehiclePanel
             is never conditionally unmounted across the 1024px breakpoint (I2b). ── */}
        <motion.div
          layout
          style={{
            position: asTopCard ? undefined : 'relative',
            width: asTopCard ? '100%' : (sidebarCollapsed ? 60 : (railNarrow ? 320 : 400)),
            minWidth: asTopCard ? undefined : (sidebarCollapsed ? 60 : (railNarrow ? 320 : 400)),
            flexShrink: asTopCard ? 0 : undefined,
            backgroundColor: '#fafafa',
            borderRight: asTopCard ? 'none' : '1px solid #e5e7eb',
            borderBottom: asTopCard ? '1px solid #e5e7eb' : 'none',
            padding: (sidebarCollapsed && !asTopCard) ? '12px 8px' : '24px 20px',
            overflowY: asTopCard ? undefined : 'auto',
          }}
        >
          {/* Heading + toggle button */}
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {(!sidebarCollapsed || asTopCard) && (
              <Text strong style={{ fontSize: '16px', color: '#374151' }}>Customer & Vehicle</Text>
            )}
            <Button type="text" size="small"
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{ color: '#6b7280', marginLeft: 'auto' }} />
          </div>

          {/* Panel body — hidden with CSS when collapsed, never unmounted */}
          <div style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
            <CustomerVehiclePanel {...cvProps} />
          </div>

          {/* Collapsed icon strip (desktop only) */}
          {sidebarCollapsed && !asTopCard && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '16px' }}><UserOutlined style={{ fontSize: '20px', color: selectedCustomer ? '#10b981' : '#d1d5db' }} /></div>
              <div><CarOutlined style={{ fontSize: '20px', color: selectedVehicle ? '#10b981' : '#d1d5db' }} /></div>
            </div>
          )}
        </motion.div>

        {/* Main content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
          <EstimateHeader
            isMobile={isMobile} isEditing={isEditing}
            selectedCustomer={selectedCustomer} selectedVehicle={selectedVehicle}
            onNavigateBack={() => navigate(`${rolePrefix}/estimates`)}
            onCustomerChange={handleCustomerChange} onVehicleChange={handleVehicleChange}
            onClearCustomer={handleClearCustomer} onClearVehicle={handleClearVehicle}
            onEditCustomer={handleOpenEditCustomer} onEditVehicle={() => setEditVehicleModalOpen(true)}
            customerCandidates={ai.customerCandidates} vehicleCandidates={ai.vehicleCandidates}
            onAcceptCustomerCandidate={ai.handleAcceptCustomerCandidate}
            onDismissCustomerCandidates={ai.handleDismissCustomerCandidates}
            onAcceptVehicleCandidate={ai.handleAcceptVehicleCandidate}
            onDismissVehicleCandidates={ai.handleDismissVehicleCandidates}
          />

          <div style={{ flex: 1, padding: isMobile ? '8px' : '24px', backgroundColor: '#f9fafb', minHeight: 0, overflowY: 'auto' }}>

            {/* Low-confidence banner */}
            <AnimatePresence>
              {(!aiAnalysis.pendingServiceItems || aiAnalysis.pendingServiceItems.length === 0) &&
               (aiAnalysis.rawServiceItemCount ?? 0) > 0 && !aiAnalysis.serviceItemsApproved && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
                  style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px',
                    border: '1px solid #ffd591', background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
                    display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <RobotOutlined style={{ color: '#fa8c16', fontSize: '14px', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: '#d46b08' }}>
                    AI detected {aiAnalysis.rawServiceItemCount} possible item{(aiAnalysis.rawServiceItemCount ?? 0) !== 1 ? 's' : ''} but all were below the 60% confidence threshold.
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            <EstimateLineItems estimateId={estimate?.id || draftEstimateId || 0}
              items={lineItems} isMobile={isMobile} isLocked={false} onItemsChange={handleLineItemsChange}
              taxRate={taxRate} vehicle={selectedVehicle || undefined} />

            {/* Look up a job in ALLDATA — HERE, on the editor.
                This lived on the read-only detail screen, which is the wrong
                place: a lookup ADDS a line, and the screen you add lines on is
                this one. The queue sits directly beneath it because there is ONE
                ALLDATA seat and a lookup runs for minutes — "nothing is
                happening" and "you are second in line" are different facts, and
                the person who pressed the button has to be able to tell them
                apart without leaving the estimate they are building. */}
            {(estimate?.id || draftEstimateId) ? (
              <Card size="small" title="ALLDATA" style={{ marginTop: 12 }}>
                <RepairDataLookupButton
                  documentType="estimate"
                  documentId={(estimate?.id || draftEstimateId) as number}
                  onQueued={() => setQueueSeq((n) => n + 1)}
                  block
                />
                <LookupQueuePanel
                  key={queueSeq}
                  bare
                  documentType="estimate"
                  documentId={(estimate?.id || draftEstimateId) as number}
                  // A finished lookup has written a line straight to the
                  // database, so what is on screen is now stale — reload rather
                  // than leave the advisor editing a copy that is missing it.
                  onCompleted={() => {
                    const eid = estimate?.id || draftEstimateId;
                    if (eid) fetchEstimate(eid);
                  }}
                />
              </Card>
            ) : (
              <Card size="small" title="ALLDATA" style={{ marginTop: 12 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Pick a customer and vehicle and add a line — the estimate has to exist before a
                  lookup can attach to it.
                </Typography.Text>
              </Card>
            )}

            {/* C2-T22: Quick catalog links */}
            {selectedVehicle && (
              <div style={{ marginTop: 12 }}>
                <QuickCatalogLinks vehicle={selectedVehicle} />
              </div>
            )}

            {/* Inspection & Photos (DVI on the estimate) */}
            <div style={{ marginTop: 12 }}>
              <EstimateInspectionSection
                estimateId={estimate?.id || draftEstimateId || 0}
                vehicle={selectedVehicle || undefined}
                onLineItemsChanged={() => { const eid = estimate?.id || draftEstimateId; if (eid) fetchEstimate(eid); }}
              />
            </div>

            {/* C2-T21: Knowledge base suggestions */}
            {selectedVehicle && (
              <div style={{ marginTop: 12 }}>
                <KnowledgeBaseSuggestions vehicle={selectedVehicle} onAdd={handleAddSuggestion} />
              </div>
            )}

            {/* Scheduling + notes — moved out of the footer so the action bar stays slim */}
            <EstimateNotesSection
              form={form}
              isMobile={isMobile}
              internalNotes={internal_notes}
              onInternalNotesChange={setInternalNotes}
            />
          </div>

          <EstimateFormFields isMobile={isMobile} loading={loading}
            isFormValid={lineItems.length > 0 && !!selectedCustomer && !!selectedVehicle} autoSaveStatus={autoSaveStatus}
            subtotal={subtotal} taxAmount={taxAmount} total={total}
            taxRateInput={taxRateInput} onTaxRateChange={handleTaxRateChange}
            onSave={handleSave} onSaveBeforeSend={handleSaveAndStay} selectedVehicle={selectedVehicle}
            selectedCustomer={selectedCustomer}
            shareUrl={estimate?.public_token ? `${window.location.origin}/estimate/${estimate.public_token}` : undefined}
            estimateId={isEditing && id ? Number(id) : draftEstimateId ?? undefined} />
        </div>
      </div>

      {/* Edit Customer Modal */}
      <Modal title="Edit Customer" open={editCustomerModalOpen}
        onCancel={() => setEditCustomerModalOpen(false)} onOk={handleSaveEditCustomer} okText="Save"
        width={window.innerWidth >= 768 ? 600 : '90%'} style={{ margin: '0 auto' }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }} centered destroyOnHidden>
        <Form form={editCustomerForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="company_name" label="Company (Optional)">
            <Input size="large" style={{ fontSize: 16 }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Vehicle Modal */}
      <Modal
        title={selectedVehicle ? `Edit ${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model}` : 'Edit Vehicle'}
        open={editVehicleModalOpen} onCancel={() => setEditVehicleModalOpen(false)} footer={null}
        width={window.innerWidth >= 768 ? 800 : '90%'} style={{ margin: '0 auto' }}
        styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }} centered destroyOnHidden>
        {selectedVehicle && (
          <VehicleForm customerId={selectedVehicle.customer_id} propVehicleId={selectedVehicle.id}
            onSuccess={handleEditVehicleSuccess} onCancel={() => setEditVehicleModalOpen(false)} modal />
        )}
      </Modal>
    </>
  );
};

export default EstimateForm;
